import { useEffect, useState } from "react";
import { Modal } from "@/components/Modals";
import { DatePicker } from "@/components/ui/date-picker";
import { LoadingState, EmptyState } from "@/components/ui/StateBoundaries";
import * as purchaseApi from "@/lib/purchaseApi";
import { inr } from "@/lib/format";

/**
 * One supplier's statement (owner, 2026-11-01): what arrived (by delivery day), what
 * went back and what was paid, with the balance after each, from an opening balance.
 */
const KIND = { bill: "Bill", return: "Return", payment: "Payment" };
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";

export default function SupplierLedger({ supplier, token, onClose }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    purchaseApi.supplierLedger(supplier.id, { fromDate, toDate }, token)
      .then(setData).catch(err => setError(reason(err)));
  }, [supplier.id, fromDate, toDate, token]);

  return (
    <Modal title={`Statement · ${supplier.name}`} onClose={onClose} testId="supplier-ledger">
      <div className="space-y-base">
        <div className="flex flex-wrap items-center gap-snug">
          <DatePicker value={fromDate} max={toDate || undefined} onChange={v => setFromDate(v || "")} ariaLabel="From" testId="ledger-from" />
          <DatePicker value={toDate} min={fromDate || undefined} onChange={v => setToDate(v || "")} ariaLabel="To" testId="ledger-to" />
        </div>
        {error && <p className="text-meta text-danger" role="alert">Statement not available: {error}</p>}
        {!error && !data && <LoadingState message="Reading the statement…" />}
        {data && (
          <>
            <p className="text-meta text-muted-foreground" data-testid="ledger-opening">Opening balance {inr(data.opening)}</p>
            {data.rows.length === 0 ? (
              <EmptyState density="inline" title="Nothing in these days" message="No bill, return or payment in this range." />
            ) : (
              <div className="rounded-lg border bg-card overflow-x-auto">
                <table className="w-full text-body">
                  <thead className="bg-muted/50 text-meta text-muted-foreground">
                    <tr>
                      <th className="px-base py-snug text-left font-medium">Date</th>
                      <th className="px-base py-snug text-left font-medium">What</th>
                      <th className="px-base py-snug text-right font-medium">Amount</th>
                      <th className="px-base py-snug text-right font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={i} className="border-t" data-testid={`ledger-row-${i}`}>
                        <td className="px-base py-snug tabular-nums">{String(row.date).slice(0, 10)}</td>
                        <td className="px-base py-snug">{KIND[row.kind]}{row.ref ? ` · ${row.ref}` : ""}</td>
                        <td className={`px-base py-snug text-right tabular-nums ${row.amount < 0 ? "text-success" : ""}`}>{inr(row.amount)}</td>
                        <td className="px-base py-snug text-right tabular-nums font-semibold">{inr(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-body font-semibold" data-testid="ledger-closing">
              {data.closing > 0 ? `You owe ${inr(data.closing)}` : data.closing < 0 ? `They owe you ${inr(-data.closing)}` : "Settled"}
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
